import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Lightning, Trophy, Crosshair, Skull, CalendarStar, Sparkle,
  ClockCounterClockwise, CaretDown,
} from "@phosphor-icons/react";

const TABS = [
  { key: "all", label: "All" },
  { key: "daily", label: "Daily" },
  { key: "ladder", label: "Ladder" },
  { key: "duel", label: "Duel" },
  { key: "survival", label: "Escape" },
];

const MODE_ICON = {
  daily: Lightning, ladder: Trophy, duel: Crosshair,
  survival: Skull, saturday: CalendarStar, championship: Sparkle,
};

export default function History() {
  const [params] = useSearchParams();
  const initialMode = TABS.some((t) => t.key === params.get("mode")) ? params.get("mode") : "all";
  const [mode, setMode] = useState(initialMode);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/history/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const load = async (m, p, append) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/history?mode=${m}&page=${p}&limit=20`);
      setItems(append ? (prev) => [...prev, ...data.items] : data.items);
      setTotal(data.total);
      setPages(data.pages);
      setPage(data.page);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(mode, 1, false); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <p className="eyebrow mb-2">Every game, every mode</p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight flex items-center gap-3">
          <ClockCounterClockwise size={26} weight="duotone" className="text-nk-accent" />
          Season History
        </h1>
      </header>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="history-summary">
          <SummaryStat label="Games played" value={summary.total_games} />
          <SummaryStat label="SS earned" value={`+${(summary.total_ss || 0).toLocaleString()}`} accent />
          <SummaryStat label="Daily shows" value={summary.modes?.daily?.games || 0} />
          <SummaryStat label="Duels" value={summary.modes?.duel?.games || 0} />
        </div>
      )}

      {/* Mode tabs */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="history-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`history-tab-${t.key}`}
            onClick={() => setMode(t.key)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              mode === t.key
                ? "bg-nk-accent text-nk-bg"
                : "border border-nk-line text-nk-mute hover:border-nk-accent hover:text-nk-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="history-list">
        {items.length === 0 && !loading && (
          <div className="nk-card p-10 text-center text-nk-mute text-sm">
            No games here yet. Play any mode and your games will show up here.
          </div>
        )}
        {items.map((it) => <HistoryRow key={it.hist_id} item={it} />)}
      </div>

      {page < pages && (
        <button
          data-testid="history-load-more"
          onClick={() => load(mode, page + 1, true)}
          disabled={loading}
          className="w-full border border-nk-line hover:border-nk-accent px-4 py-3 rounded-sm text-sm text-nk-mute hover:text-nk-text transition-colors inline-flex items-center justify-center gap-2"
        >
          {loading ? "Loading…" : <>Load more <CaretDown size={12} /></>}
          <span className="text-[10px] font-mono text-nk-dim">({items.length}/{total})</span>
        </button>
      )}
    </div>
  );
}

function SummaryStat({ label, value, accent }) {
  return (
    <div className="nk-card p-4">
      <p className="text-[10px] uppercase tracking-wider text-nk-dim">{label}</p>
      <p className={`font-display text-2xl mt-1 ${accent ? "text-nk-accent" : ""}`}>{value}</p>
    </div>
  );
}

function HistoryRow({ item }) {
  const Icon = MODE_ICON[item.mode] || Lightning;
  const { title, sub, badge, badgeTone } = describe(item);
  return (
    <div className="nk-card px-4 py-3 flex items-center gap-3" data-testid={`history-row-${item.mode}`}>
      <Icon size={18} weight="duotone" className="text-nk-accent shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{title}</p>
        <p className="text-[11px] text-nk-mute truncate">{sub}</p>
      </div>
      <div className="text-right shrink-0">
        {badge && (
          <p className={`text-xs font-medium ${badgeTone}`}>{badge}</p>
        )}
        <p className="text-[10px] text-nk-dim font-mono mt-0.5">{formatWhen(item.created_at)}</p>
      </div>
    </div>
  );
}

function describe(it) {
  const ss = it.ss_earned != null ? `+${(it.ss_earned || 0).toLocaleString()} SS` : "";
  switch (it.mode) {
    case "daily":
      return {
        title: `NK Daily — ${it.correct ?? 0}/${it.total ?? 15} correct`,
        sub: `${it.fastest_wins || 0} fastest answer${(it.fastest_wins || 0) === 1 ? "" : "s"} · ${ss}`,
        badge: ss, badgeTone: "text-nk-accent",
      };
    case "ladder": {
      const label =
        it.outcome === "full_clear" ? "Full clear · 15 rungs" :
        it.outcome === "cashed_out" ? `Cashed out · rung ${it.rungs_cleared}` :
        it.outcome === "quit" ? `Walked away · rung ${it.rungs_cleared}` :
        it.outcome === "timeout" ? `Timed out · ${it.rungs_cleared} cleared` :
        `Fell · ${it.rungs_cleared} cleared`;
      return {
        title: `NK Ladder — ${label}`,
        sub: ss,
        badge: ss, badgeTone: it.outcome === "full_clear" ? "text-nk-gold" : "text-nk-accent",
      };
    }
    case "duel": {
      const res = it.result === "win" ? "Win" : it.result === "loss" ? "Loss" : it.result === "tie" ? "Tie" : "Solo";
      const tone = it.result === "win" ? "text-nk-accent" : it.result === "loss" ? "text-nk-bad" : "text-nk-mute";
      const delta = typeof it.rating_delta === "number" && it.rating_delta !== 0
        ? ` · ${it.rating_delta > 0 ? "+" : ""}${it.rating_delta} ELO` : "";
      return {
        title: `NK Duel vs ${it.opponent || "—"} — ${it.my_score ?? 0}${it.opp_score != null ? `–${it.opp_score}` : ""}`,
        sub: `${ss}${delta}`,
        badge: res, badgeTone: tone,
      };
    }
    case "survival":
      return {
        title: `NK Escape — streak of ${it.streak ?? 0}`,
        sub: `${it.outcome === "quit" ? "Walked away" : "Run ended"} · ${ss}${it.new_record ? " · ★ new record" : ""}`,
        badge: ss, badgeTone: "text-nk-accent",
      };
    default:
      return { title: it.mode, sub: ss, badge: ss, badgeTone: "text-nk-accent" };
  }
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
